import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './JwtParser.module.css';

type JwtPayload = Record<string, unknown>;

const decodeBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(padLength);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const formatUnixTime = (value: unknown): string => {
  if (typeof value !== 'number') return '-';
  const d = new Date(value * 1000);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.toLocaleString()} (${value})`;
};

const JwtParser: React.FC = () => {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [payloadText, setPayloadText] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const showMessage = (text: string, error = false) => {
    setMessage(text);
    setIsError(error);
    setTimeout(() => {
      setMessage('');
      setIsError(false);
    }, 3000);
  };

  const handleParse = () => {
    if (!token.trim()) {
      showMessage(t('jwtParser.messageTokenRequired'), true);
      return;
    }

    try {
      const parts = token.trim().split('.');
      if (parts.length !== 3) {
        showMessage(t('jwtParser.messageFormatInvalid'), true);
        return;
      }

      const header = JSON.parse(decodeBase64Url(parts[0])) as JwtPayload;
      const payload = JSON.parse(decodeBase64Url(parts[1])) as JwtPayload;
      const signature = parts[2];

      const expText = formatUnixTime(payload.exp);
      const iatText = formatUnixTime(payload.iat);
      const nbfText = formatUnixTime(payload.nbf);

      setHeaderText(JSON.stringify(header, null, 2));
      setPayloadText(
        `${JSON.stringify(payload, null, 2)}\n\n${t('jwtParser.commonTimeFieldsTitle')}\nexp: ${expText}\niat: ${iatText}\nnbf: ${nbfText}`
      );
      setSignatureText(signature);
      showMessage(t('jwtParser.messageParseSuccess'));
    } catch {
      showMessage(t('jwtParser.messageParseFailed'), true);
    }
  };

  const handleClear = () => {
    setToken('');
    setHeaderText('');
    setPayloadText('');
    setSignatureText('');
    setMessage('');
    setIsError(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t('jwtParser.title')}</h2>
        <p className={styles.description}>{t('jwtParser.description')}</p>

        {message && <div className={`${styles.tip} ${isError ? styles.error : styles.success}`}>{message}</div>}

        <div className={styles.label}>{t('jwtParser.labelToken')}</div>
        <textarea
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className={styles.tokenArea}
          placeholder={t('jwtParser.placeholderToken')}
        />

        <div className={styles.actions}>
          <button onClick={handleParse} className={styles.primaryButton}>
            {t('jwtParser.btnParse')}
          </button>
          <button onClick={handleClear} className={styles.secondaryButton}>
            {t('jwtParser.btnClear')}
          </button>
        </div>

        <div className={styles.resultGrid}>
          <div>
            <div className={styles.label}>{t('jwtParser.labelHeader')}</div>
            <textarea value={headerText} readOnly className={styles.resultArea} placeholder={t('jwtParser.placeholderParsedHeader')} />
          </div>
          <div>
            <div className={styles.label}>{t('jwtParser.labelPayload')}</div>
            <textarea value={payloadText} readOnly className={styles.resultArea} placeholder={t('jwtParser.placeholderParsedPayload')} />
          </div>
        </div>

        <div>
          <div className={styles.label}>{t('jwtParser.labelSignature')}</div>
          <textarea value={signatureText} readOnly className={styles.signatureArea} placeholder={t('jwtParser.placeholderSignature')} />
        </div>
      </div>
    </div>
  );
};

export default JwtParser;
